import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, CheckCircle2, Clock, AlertCircle, User,
  Calendar, Tag, MoreVertical, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useDepartmentStore, useTaskStore, useUIStore } from '../lib/store';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';

const UserTasksPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { userTasks, fetchUserTasks } = useDepartmentStore();
  const { updateTask } = useTaskStore();
  const { language } = useUIStore();
  const { t } = useTranslation(language);
  
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadUserTasks = async () => {
      try {
        await fetchUserTasks(userId);
      } catch (error) {
        toast.error(error.message || t('error'));
      } finally {
        setIsLoading(false);
      }
    };
    
    if (userId) {
      loadUserTasks();
    }
  }, [userId, fetchUserTasks, t]);

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await updateTask(taskId, { status: newStatus });
      await fetchUserTasks(userId); // Refresh data
      toast.success(language === 'hi' ? 'टास्क अपडेट हुआ' : 'Task updated');
    } catch (error) {
      toast.error(error.message || t('error'));
    }
  };

  const columns = [
    { id: 'todo', title: t('todo'), color: 'bg-muted-foreground', icon: Clock },
    { id: 'in_progress', title: t('in_progress'), color: 'bg-chart-4', icon: AlertCircle },
    { id: 'done', title: t('done'), color: 'bg-chart-3', icon: CheckCircle2 },
  ];

  // Calculate stats
  const stats = userTasks ? {
    total: userTasks.total || 0,
    todo: userTasks.tasks?.todo?.length || 0,
    in_progress: userTasks.tasks?.in_progress?.length || 0,
    done: userTasks.tasks?.done?.length || 0,
  } : { total: 0, todo: 0, in_progress: 0, done: 0 };
  
  const completionRate = stats.total > 0 ? Math.round((stats.done / stats.total) * 100) : 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="user-tasks-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="h-10 w-10"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-4">
          <Avatar className="h-14 w-14 border-2 border-primary/30">
            <AvatarFallback className="bg-primary/20 text-primary text-xl font-bold">
              {userTasks?.user?.name?.substring(0, 2).toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          <div>
            <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
              <User className="h-6 w-6 text-primary" />
              {userTasks?.user?.name || 'User'}
            </h1>
            <p className="text-muted-foreground">
              {userTasks?.user?.email || ''}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="glass">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">
                    {language === 'hi' ? 'कुल टास्क' : 'Total Tasks'}
                  </p>
                  <p className="font-heading text-2xl font-bold">{stats.total}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Tag className="h-5 w-5 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="glass">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">{t('todo')}</p>
                  <p className="font-heading text-2xl font-bold">{stats.todo}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-muted-foreground/20 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-muted-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="glass">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">{t('in_progress')}</p>
                  <p className="font-heading text-2xl font-bold">{stats.in_progress}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-chart-4/20 flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-chart-4" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="glass">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">{t('done')}</p>
                  <p className="font-heading text-2xl font-bold">{stats.done}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-chart-3/20 flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-chart-3" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Completion Progress */}
      <Card className="glass">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">
              {language === 'hi' ? 'पूर्णता दर' : 'Completion Rate'}
            </span>
            <span className="text-lg font-bold text-primary">{completionRate}%</span>
          </div>
          <Progress value={completionRate} className="h-2" />
        </CardContent>
      </Card>

      {/* Kanban Board */}
      <div className="grid grid-cols-3 gap-4">
        {columns.map((column, colIndex) => {
          const tasks = userTasks?.tasks?.[column.id] || [];
          const Icon = column.icon;
          
          return (
            <motion.div
              key={column.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 + colIndex * 0.1 }}
            >
              <Card className="glass min-h-[400px]">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <div className={`h-2 w-2 rounded-full ${column.color}`} />
                    <Icon className="h-4 w-4" />
                    {column.title}
                    <span className="text-muted-foreground font-normal">({tasks.length})</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[350px] pr-2">
                    <div className="space-y-3">
                      {tasks.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-8">
                          {language === 'hi' ? 'कोई टास्क नहीं' : 'No tasks'}
                        </p>
                      ) : (
                        tasks.map((task, taskIndex) => (
                          <motion.div
                            key={task.id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.6 + taskIndex * 0.05 }}
                            className="p-3 rounded-lg bg-card/50 border border-white/5 hover:border-white/10 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                              <p className="font-medium text-sm flex-1">{task.title}</p>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-6 w-6 -mr-1">
                                    <MoreVertical className="h-3 w-3" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {column.id !== 'todo' && (
                                    <DropdownMenuItem onClick={() => handleStatusChange(task.id, 'todo')}>
                                      <Clock className="h-4 w-4 mr-2" />
                                      {t('todo')}
                                    </DropdownMenuItem>
                                  )}
                                  {column.id !== 'in_progress' && (
                                    <DropdownMenuItem onClick={() => handleStatusChange(task.id, 'in_progress')}>
                                      <AlertCircle className="h-4 w-4 mr-2" />
                                      {t('in_progress')}
                                    </DropdownMenuItem>
                                  )}
                                  {column.id !== 'done' && (
                                    <DropdownMenuItem onClick={() => handleStatusChange(task.id, 'done')}>
                                      <CheckCircle2 className="h-4 w-4 mr-2" />
                                      {t('done')}
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                            
                            {task.description && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                {task.description}
                              </p>
                            )}
                            
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                task.priority === 'high' ? 'bg-destructive/20 text-destructive' :
                                task.priority === 'medium' ? 'bg-chart-4/20 text-chart-4' :
                                'bg-muted text-muted-foreground'
                              }`}>
                                {t(task.priority)}
                              </span>
                              {task.due_date && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {new Date(task.due_date).toLocaleDateString()}
                                </span>
                              )}
                              {task.project_name && (
                                <span className="text-xs text-primary/70">
                                  {task.project_name}
                                </span>
                              )}
                            </div>
                          </motion.div>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default UserTasksPage;
